import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import * as ContextMenu from "@radix-ui/react-context-menu";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { Ico } from "./Icons";
import AdCard from "@/shared/components/AdCard";
import { useDroppable } from "@dnd-kit/core";
import { contentCatalogStore } from "../contentCatalogStore";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { appStore } from "@store/appStore";
import {
    createCatalogDragId,
    createCatalogDropId,
    type CatalogDragItem,
    type CatalogDropTarget,
    type CatalogSection,
} from "../contentCatalogDnd";
import { Ad } from "@models/Ad.model";
import ConfirmActionModal from "./ConfirmActionModal";
import RenameActionModal from "./RenameActionModal";
import type { AdMenuItemsFn, FolderMenuItemsFn } from "../contentCatalog";
import { borrowedOwnerName } from "../folderOwner";

function findFolderById(nodes: ContentCatalogFolder[], folderId: number): ContentCatalogFolder | null {
    for (const node of nodes) {
        if (node.id === folderId) return node;
        const found = findFolderById(node.children || [], folderId);
        if (found) return found;
    }
    return null;
}

function removeAdFromFolder(nodes: ContentCatalogFolder[], folderId: number, adId: number): boolean {
    const folder = findFolderById(nodes, folderId);
    if (!folder) return false;

    const prevLength = folder.ads.length;
    folder.ads = folder.ads.filter((ad) => ad.id !== adId);
    return folder.ads.length !== prevLength;
}

function removeAdFromTree(nodes: ContentCatalogFolder[], adId: number): boolean {
    let removed = false;
    for (const node of nodes) {
        const originalLength = node.ads.length;
        node.ads = node.ads.filter((ad) => ad.id !== adId);
        if (node.ads.length !== originalLength) removed = true;
        if (removeAdFromTree(node.children || [], adId)) removed = true;
    }
    return removed;
}

function removeFolderById(nodes: ContentCatalogFolder[], folderId: number): boolean {
    const idx = nodes.findIndex((n) => n.id === folderId);
    if (idx >= 0) {
        nodes.splice(idx, 1);
        return true;
    }
    for (const node of nodes) {
        if (removeFolderById(node.children || [], folderId)) return true;
    }
    return false;
}

function renameAdInTree(nodes: ContentCatalogFolder[], adId: number, nextName: string): boolean {
    let changed = false;
    for (const node of nodes) {
        for (const ad of node.ads || []) {
            if (ad.id === adId) {
                ad.name = nextName;
                changed = true;
            }
        }

        if (renameAdInTree(node.children || [], adId, nextName)) {
            changed = true;
        }
    }

    return changed;
}

// ─── Thin DND wrapper for the folder header — re-renders during DND but is cheap ───

function DroppableAccordionHeader({
    node,
    section,
    parentFolderId,
    depth,
    open,
    hasChildren,
    hasAds,
    enableDnd,
    onToggle,
    folderActions,
    onCreateSubfolder,
    onCreateAd,
    onRenameFolder,
    onDeleteFolder,
}: {
    node: ContentCatalogFolder;
    section: CatalogSection;
    parentFolderId: number | null;
    depth: number;
    open: boolean;
    hasChildren: boolean;
    hasAds: boolean;
    enableDnd?: boolean;
    onToggle: () => void;
    folderActions?: { label: string; onSelect: () => void; danger?: boolean; disabled?: boolean }[];
    onCreateSubfolder: () => void;
    onCreateAd: () => void;
    onRenameFolder: () => void;
    onDeleteFolder: () => void;
}) {
    const { organizationId } = useSnapshot(contentCatalogStore);
    const ownerName = borrowedOwnerName(node, organizationId);

    const folderDropData: CatalogDropTarget = {
        kind: "folder-item",
        section,
        folderId: node.id,
        parentFolderId,
    };

    const {
        setNodeRef: setFolderDropRef,
        isOver: isFolderDropOver,
    } = useDroppable({
        id: createCatalogDropId("main", folderDropData, `${section}-folder-${node.id}`),
        data: folderDropData,
        disabled: !enableDnd,
    });

    const badgeText = hasChildren
        ? `${node.children.length} folder${node.children.length !== 1 ? "s" : ""}`
        : hasAds
            ? `${node.ads.length} ad${node.ads.length !== 1 ? "s" : ""}`
            : "Empty";

    const skipFolderMenu = folderActions && folderActions.length === 0;

    const headerEl = (
        <div
            ref={setFolderDropRef}
            className={`col-accordion-header${open ? " open" : ""}${isFolderDropOver ? " cc-drop--over" : ""}`}
            onClick={onToggle}
        >
            <span className="col-accordion-caret">
                {open ? <Ico.ChevronDown size={13} /> : <Ico.ChevronRight size={13} />}
            </span>
            <span className="col-accordion-icon">
                {depth === 0 && section === "collection" ? <Ico.CollectionFilled /> : <Ico.FolderClosed />}
            </span>
            <span className="col-accordion-name">
                <span className="col-accordion-title">{node.name}</span>
                {ownerName && <span className="col-accordion-owner">{ownerName}</span>}
            </span>
            <span className="col-accordion-badge">{badgeText}</span>
        </div>
    );

    if (skipFolderMenu) return headerEl;

    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger asChild>{headerEl}</ContextMenu.Trigger>
            <ContextMenu.Portal>
                <ContextMenu.Content className="radix-context-menu-content" alignOffset={2} style={{ zIndex: 2000 }}>
                    {folderActions ? (
                        folderActions.map((action) => (
                            <ContextMenu.Item
                                key={action.label}
                                className={[
                                    "radix-context-menu-item",
                                    action.danger ? "radix-context-menu-item--danger" : "",
                                ].join(" ").trim()}
                                onSelect={action.onSelect}
                                disabled={action.disabled}
                            >
                                {action.label}
                            </ContextMenu.Item>
                        ))
                    ) : (
                        <>
                            <ContextMenu.Item
                                className="radix-context-menu-item"
                                onSelect={onCreateSubfolder}
                            >
                                New Folder
                            </ContextMenu.Item>
                            <ContextMenu.Item
                                className="radix-context-menu-item"
                                onSelect={onCreateAd}
                            >
                                New Ad
                            </ContextMenu.Item>
                            <ContextMenu.Item
                                className="radix-context-menu-item"
                                onSelect={onRenameFolder}
                            >
                                Rename Folder
                            </ContextMenu.Item>
                            <ContextMenu.Item
                                className="radix-context-menu-item radix-context-menu-item--danger"
                                onSelect={onDeleteFolder}
                            >
                                Delete Folder
                            </ContextMenu.Item>
                        </>
                    )}
                </ContextMenu.Content>
            </ContextMenu.Portal>
        </ContextMenu.Root>
    );
}

// ─── Memoized accordion body — no DND hooks, never re-renders during drag ───

const AccordionBody = memo(function AccordionBody({
    node,
    section,
    depth,
    view,
    hasAds,
    hasChildren,
    selectedAdIds,
    dndByAdId,
    defaultMenuByAdId,
    adMenuItems,
    useCustomAdMenu,
    onAdClick,
    folderMenuItems,
    enableDnd,
}: {
    node: ContentCatalogFolder;
    section: CatalogSection;
    depth: number;
    view: string;
    hasAds: boolean;
    hasChildren: boolean;
    selectedAdIds: number[];
    dndByAdId: Map<number, any> | null;
    defaultMenuByAdId: Map<number, any> | null;
    adMenuItems?: AdMenuItemsFn;
    useCustomAdMenu: boolean;
    onAdClick?: (ad: Ad) => void;
    folderMenuItems?: FolderMenuItemsFn;
    enableDnd?: boolean;
}) {
    return (
        <div className="col-accordion-body">
            {hasAds && (
                <div className={[
                    "col-ad-grid",
                    view === "list" ? "col-ad-grid--list" : "",
                ].join(" ").trim()}>
                    {node.ads.map((ad) => {
                        const card = (
                            <AdCard
                                key={ad.id}
                                ad={ad}
                                mode="detail"
                                dnd={dndByAdId?.get(ad.id)}
                                isSelected={selectedAdIds.includes(ad.id)}
                                onClick={() => onAdClick?.(ad)}
                                menuActions={useCustomAdMenu ? adMenuItems!(ad, node.id, section) : defaultMenuByAdId?.get(ad.id)}
                            />
                        );
                        return view === "list"
                            ? <div key={ad.id} className="cc-adcard-list-wrapper">{card}</div>
                            : card;
                    })}
                </div>
            )}
            {hasChildren && (
                <div className="col-accordion-children">
                    {node.children.map((child) => (
                        <CollectionAccordion
                            key={child.id}
                            node={child}
                            section={section}
                            depth={depth + 1}
                            parentFolderId={node.id}
                            view={view}
                            onAdClick={onAdClick}
                            adMenuItems={adMenuItems}
                            folderMenuItems={folderMenuItems}
                            enableDnd={enableDnd}
                        />
                    ))}
                </div>
            )}
            {!hasChildren && !hasAds && (
                <p className="col-accordion-empty">Nothing in this collection yet.</p>
            )}
        </div>
    );
});

// ─── Main component — no DND hooks ───

export default function CollectionAccordion({
    node,
    section = "collection",
    depth = 0,
    parentFolderId = null,
    view = "grid",
    onAdClick,
    adMenuItems,
    folderMenuItems,
    enableDnd,
}: {
    node: ContentCatalogFolder;
    section?: CatalogSection;
    depth?: number;
    parentFolderId?: number | null;
    view?: string;
    onAdClick?: (ad: Ad) => void;
    adMenuItems?: AdMenuItemsFn;
    folderMenuItems?: FolderMenuItemsFn;
    enableDnd?: boolean;
}) {
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const { selectedAdIds, folderSearch, query } = useSnapshot(contentCatalogStore);
    const organizationId = appStore.loggedInUser.organization.id;
    const [open, setOpen] = useState(true);
    const [pendingRemoveAdId, setPendingRemoveAdId] = useState<number | null>(null);
    const [adPendingRemove, setAdPendingRemove] = useState<Ad | null>(null);
    const [adPendingRename, setAdPendingRename] = useState<Ad | null>(null);
    const [renamingAdId, setRenamingAdId] = useState<number | null>(null);
    const [folderPendingRename, setFolderPendingRename] = useState(false);
    const [folderPendingDelete, setFolderPendingDelete] = useState(false);
    const [folderBusy, setFolderBusy] = useState(false);
    const useCustomAdMenu = !!adMenuItems;

    const isSearchingAll = section === "all" && (query || "").trim().length > 0;

    // Auto-expand folders that match search
    const prevAutoExpand = useRef(false);
    useEffect(() => {
        if (isSearchingAll && folderSearch.autoExpandFolderIds.has(node.id)) {
            setOpen(true);
            prevAutoExpand.current = true;
        } else if (prevAutoExpand.current && !isSearchingAll) {
            prevAutoExpand.current = false;
        }
    }, [isSearchingAll, folderSearch.autoExpandFolderIds, node.id]);

    // Filter node for search: filter ads and children when section === "all" and searching
    const filteredNode = useMemo(() => {
        if (!isSearchingAll) return node;

        const visibleAdIds = folderSearch.visibleAdIdsByFolder.get(node.id);
        const filteredAds = visibleAdIds
            ? node.ads.filter((ad) => visibleAdIds.has(ad.id))
            : node.ads;

        const filteredChildren = node.children?.filter((child) =>
            folderSearch.visibleFolderIds.has(child.id),
        ) || [];

        return { ...node, ads: filteredAds, children: filteredChildren };
    }, [isSearchingAll, node, folderSearch.visibleAdIdsByFolder, folderSearch.visibleFolderIds]);

    const hasChildren = filteredNode.children?.length > 0;
    const hasAds = filteredNode.ads?.length > 0;

    const isDeleteMode = section === "all";

    const handleRemoveAd = async (ad: Ad) => {
        if (pendingRemoveAdId !== null) return;

        setPendingRemoveAdId(ad.id);
        try {
            if (isDeleteMode) {
                await ccService.deleteAd(ad.id);
                const removedAll = removeAdFromTree(contentCatalogStore.folders as ContentCatalogFolder[], ad.id);
                const removedCollections = removeAdFromTree(contentCatalogStore.collections as ContentCatalogFolder[], ad.id);
                if (removedAll) contentCatalogStore.folders = [...(contentCatalogStore.folders as ContentCatalogFolder[])];
                if (removedCollections) contentCatalogStore.collections = [...(contentCatalogStore.collections as ContentCatalogFolder[])];
            } else {
                await ccService.unlinkAds(organizationId, node.id, [ad.id]);
                const removed = removeAdFromFolder(contentCatalogStore.collections as ContentCatalogFolder[], node.id, ad.id);
                if (removed) {
                    contentCatalogStore.collections = [...(contentCatalogStore.collections as ContentCatalogFolder[])];
                }
            }
            setAdPendingRemove(null);
        } catch (error) {
            console.error(isDeleteMode ? "Failed to delete ad:" : "Failed to remove ad from collection:", error);
        } finally {
            setPendingRemoveAdId(null);
        }
    };

    const handleRenameAd = async (nextName: string) => {
        if (!adPendingRename) return;
        try {
            setRenamingAdId(adPendingRename.id);
            await ccService.updateAd(adPendingRename.id, { name: nextName });
            const allContentChanged = renameAdInTree(contentCatalogStore.folders as ContentCatalogFolder[], adPendingRename.id, nextName);
            const collectionsChanged = renameAdInTree(contentCatalogStore.collections as ContentCatalogFolder[], adPendingRename.id, nextName);

            if (allContentChanged) {
                contentCatalogStore.folders = [...(contentCatalogStore.folders as ContentCatalogFolder[])];
            }

            if (collectionsChanged) {
                contentCatalogStore.collections = [...(contentCatalogStore.collections as ContentCatalogFolder[])];
            }
            setAdPendingRename(null);
        } catch (error) {
            console.error("Failed to rename ad:", error);
        } finally {
            setRenamingAdId(null);
        }
    };

    const storeKey = section === "collection" ? "collections" : "folders";

    const handleCreateSubfolder = async () => {
        if (folderBusy) return;
        setFolderBusy(true);
        try {
            const created = await ccService.createFolder(organizationId, {
                name: section === "collection" ? "New Collection" : "New Folder",
                type: section === "collection" ? "collection" : "folder",
                parent_id: node.id,
            });
            const newFolder: ContentCatalogFolder = { ...created, children: [], ads: [] };
            const roots = contentCatalogStore[storeKey] as ContentCatalogFolder[];
            const parent = findFolderById(roots, node.id);
            if (parent) {
                parent.children = [...(parent.children || []), newFolder];
                contentCatalogStore[storeKey] = [...roots];
            }
            setOpen(true);
            requestAnimationFrame(() => {
                const el = document.querySelector(`[data-folder-id="${newFolder.id}"]`);
                el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            });
        } catch (error) {
            console.error("Failed to create folder:", error);
        } finally {
            setFolderBusy(false);
        }
    };

    const handleCreateAd = async () => {
        if (folderBusy) return;
        setFolderBusy(true);
        try {
            const created = await ccService.createAd({
                name: "New Ad",
                type: "container",
                subtype: "container",
                parent_id: node.id,
            });
            const newAd = new Ad(created);
            const roots = contentCatalogStore[storeKey] as ContentCatalogFolder[];
            const folder = findFolderById(roots, node.id);
            if (folder) {
                folder.ads = [...folder.ads, newAd];
                contentCatalogStore[storeKey] = [...roots];
            }
            setOpen(true);
        } catch (error) {
            console.error("Failed to create ad:", error);
        } finally {
            setFolderBusy(false);
        }
    };

    const handleRenameFolder = async (nextName: string) => {
        if (folderBusy) return;
        setFolderBusy(true);
        try {
            await ccService.updateFolder(organizationId, node.id, { name: nextName });
            const roots = contentCatalogStore[storeKey] as ContentCatalogFolder[];
            const folder = findFolderById(roots, node.id);
            if (folder) {
                folder.name = nextName;
                contentCatalogStore[storeKey] = [...roots];
            }
        } catch (error) {
            console.error("Failed to rename folder:", error);
        } finally {
            setFolderBusy(false);
            setFolderPendingRename(false);
        }
    };

    const handleDeleteFolder = async () => {
        if (folderBusy) return;
        setFolderBusy(true);
        try {
            await ccService.deleteFolder(organizationId, node.id);
            const roots = contentCatalogStore[storeKey] as ContentCatalogFolder[];
            removeFolderById(roots, node.id);
            contentCatalogStore[storeKey] = [...roots];
            if (contentCatalogStore.selectedId === String(node.id)) {
                contentCatalogStore.selectedId = section === "collection" ? "__collections__" : "__all__";
            }
        } catch (error) {
            console.error("Failed to delete folder:", error);
        } finally {
            setFolderBusy(false);
            setFolderPendingDelete(false);
        }
    };

    const dndByAdId = useMemo(() => {
        if (!enableDnd) return null;
        const map = new Map<number, { dragId: string; dragData: CatalogDragItem; dropId: string; dropData: CatalogDropTarget }>();
        for (const ad of filteredNode.ads || []) {
            const dragData: CatalogDragItem = {
                kind: "ad",
                source: "main",
                section,
                adId: ad.id,
                parentFolderId: node.id,
            };
            const dropData: CatalogDropTarget = {
                kind: "ad-item",
                section,
                adId: ad.id,
                parentFolderId: node.id,
            };
            map.set(ad.id, {
                dragId: createCatalogDragId(dragData),
                dragData,
                dropId: createCatalogDropId("main", dropData, `${section}-ad-item-${node.id}-${ad.id}`),
                dropData,
            });
        }
        return map;
    }, [enableDnd, filteredNode.ads, node.id, section]);

    const defaultMenuByAdId = useMemo(() => {
        if (useCustomAdMenu) return null;
        const map = new Map<number, { label: string; onSelect: () => void; danger?: boolean; disabled?: boolean }[]>();
        for (const ad of filteredNode.ads || []) {
            map.set(ad.id, [
                { label: "Edit Ad", onSelect: () => { onAdClick?.(ad); } },
                { label: "Rename Ad", onSelect: () => { setAdPendingRename(ad); } },
                {
                    label: isDeleteMode ? "Delete Ad" : "Remove Ad",
                    onSelect: () => { setAdPendingRemove(ad); },
                    danger: true,
                    disabled: pendingRemoveAdId !== null,
                },
            ]);
        }
        return map;
    }, [useCustomAdMenu, filteredNode.ads, node.id, isDeleteMode, pendingRemoveAdId, onAdClick]);

    const folderActions = folderMenuItems?.(node, section);

    return (
        <div className={`col-accordion${depth > 0 ? " col-accordion--nested" : ""}`} data-folder-id={node.id}>
            <DroppableAccordionHeader
                node={node}
                section={section}
                parentFolderId={parentFolderId}
                depth={depth}
                open={open}
                hasChildren={hasChildren}
                hasAds={hasAds}
                enableDnd={enableDnd}
                onToggle={() => setOpen((p) => !p)}
                folderActions={folderActions}
                onCreateSubfolder={() => void handleCreateSubfolder()}
                onCreateAd={() => void handleCreateAd()}
                onRenameFolder={() => setFolderPendingRename(true)}
                onDeleteFolder={() => setFolderPendingDelete(true)}
            />

            {open && (
                <AccordionBody
                    node={filteredNode as ContentCatalogFolder}
                    section={section}
                    depth={depth}
                    view={view}
                    hasAds={hasAds}
                    hasChildren={hasChildren}
                    selectedAdIds={selectedAdIds as number[]}
                    dndByAdId={dndByAdId}
                    defaultMenuByAdId={defaultMenuByAdId}
                    adMenuItems={adMenuItems}
                    useCustomAdMenu={useCustomAdMenu}
                    onAdClick={onAdClick}
                    folderMenuItems={folderMenuItems}
                    enableDnd={enableDnd}
                />
            )}
            {!useCustomAdMenu && (
                <>
                    <ConfirmActionModal
                        show={adPendingRemove !== null}
                        title={isDeleteMode ? "Delete Ad" : "Remove Ad"}
                        message={adPendingRemove
                            ? (isDeleteMode
                                ? `Delete "${adPendingRemove.name || `Ad ${adPendingRemove.id}`}"?`
                                : `Remove "${adPendingRemove.name || `Ad ${adPendingRemove.id}`}" from this collection?`)
                            : ""}
                        confirmLabel={isDeleteMode ? "Delete" : "Remove"}
                        onConfirm={() => {
                            if (!adPendingRemove) return;
                            void handleRemoveAd(adPendingRemove);
                        }}
                        onClose={() => {
                            if (pendingRemoveAdId === null) setAdPendingRemove(null);
                        }}
                        isLoading={pendingRemoveAdId !== null}
                    />
                    <RenameActionModal
                        show={adPendingRename !== null}
                        title="Rename Ad"
                        initialValue={adPendingRename?.name || (adPendingRename ? `Ad ${adPendingRename.id}` : "")}
                        confirmLabel="Rename"
                        onConfirm={(nextName) => { void handleRenameAd(nextName); }}
                        onClose={() => {
                            if (renamingAdId === null) setAdPendingRename(null);
                        }}
                        isLoading={renamingAdId !== null}
                    />
                </>
            )}
            {!folderMenuItems && (
                <>
                    <ConfirmActionModal
                        show={folderPendingDelete}
                        title="Delete Folder"
                        message={`Delete "${node.name}"? All contents will be removed.`}
                        confirmLabel="Delete"
                        onConfirm={() => { void handleDeleteFolder(); }}
                        onClose={() => { if (!folderBusy) setFolderPendingDelete(false); }}
                        isLoading={folderBusy}
                    />
                    <RenameActionModal
                        show={folderPendingRename}
                        title="Rename Folder"
                        initialValue={node.name}
                        confirmLabel="Rename"
                        onConfirm={(nextName) => { void handleRenameFolder(nextName); }}
                        onClose={() => { if (!folderBusy) setFolderPendingRename(false); }}
                        isLoading={folderBusy}
                    />
                </>
            )}
        </div>
    );
}
